/**
 * "Add to home screen" for the trip page.
 *
 * The two platforms are not symmetrical, and the difference drives the design:
 *
 *  - Android/Chrome fires `beforeinstallprompt`, which can be deferred and
 *    replayed from our own button — a real one-tap install.
 *  - iOS/Safari has no install API at all. The only route is Share → Add to
 *    Home Screen, done by hand, so all we can do is say so clearly.
 *
 * The button therefore never promises an install it cannot deliver: if there is
 * no deferred prompt (iOS, or a Chrome that declined to offer one), tapping it
 * shows that platform's steps instead of failing silently.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'bixl-install-dismissed';
const DISMISS_DAYS = 30;

/**
 * Dismissal expires. It used to be permanent, which was unrecoverable without
 * clearing site data — and the first version made it far too easy to trigger by
 * accident. The stored value is now a timestamp; the old permanent marker ("1")
 * parses as 1ms after the epoch, so it reads as long expired and a phone stuck
 * in that state gets the banner back on its own.
 *
 * localStorage throws outright in some privacy modes, so every access is guarded.
 */
function wasDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function rememberDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    // A banner that reappears next visit is a much smaller problem than a crash.
  }
}

/** Already installed: the page is running from the home-screen icon. */
function isStandalone(): boolean {
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; the touch points give it away.
  return /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/** A phone or tablet — where a home-screen icon is the point. */
function isHandheld(): boolean {
  return navigator.maxTouchPoints > 0 && window.matchMedia('(max-width: 1024px)').matches;
}

/**
 * Why the banner is not on screen, or null if it should be.
 *
 * Every one of these used to be a silent early return, which made "it just
 * never shows up" impossible to tell apart from a broken build.
 */
function suppressionReason(): string | null {
  if (isStandalone()) return 'already installed — this page is running standalone';
  if (wasDismissed()) return `dismissed in the last ${DISMISS_DAYS} days (localStorage ${DISMISSED_KEY})`;
  if (!isHandheld()) return 'not a phone or tablet — a home-screen icon means nothing here';
  return null;
}

/**
 * Escape hatch: shows the banner whatever the gates say, and reports which gate
 * would have hidden it. Also the cure for a dismissal you want back early.
 */
function isForced(): boolean {
  return new URLSearchParams(window.location.search).has('install');
}

const IOS_BOOKMARK_STEPS = [
  'Tap the Share button at the bottom of Safari.',
  'Choose “Add Bookmark”, then tap “Save”.',
  'To reopen: tap the book icon at the bottom of Safari, then Bookmarks.',
];

const ANDROID_BOOKMARK_STEPS = [
  'Open the ⋮ menu at the top right of Chrome.',
  'Tap the ☆ star icon.',
  'To reopen: ⋮ menu → Bookmarks.',
];

const IOS_STEPS = [
  'Tap the Share button at the bottom of Safari.',
  'Scroll down and choose “Add to Home Screen”.',
  'Tap “Add” — the icon lands on your home screen.',
  'Open the icon once and enter the code again — it has its own private storage. It stays signed in after that.',
];

const ANDROID_STEPS = [
  'Open the ⋮ menu at the top right of Chrome.',
  'Choose “Add to Home screen” (or “Install app”).',
  'Confirm — the icon lands on your home screen.',
];

/**
 * Points the installed icon at THIS trip rather than the site root.
 *
 * The static manifest has to say start_url "/", which would open the landing
 * page — useless, since it only tells you to open your link. A per-trip manifest
 * is built as a blob and swapped in. Its URLs must be absolute: relative paths
 * would resolve against the blob's own opaque URL.
 *
 * Done as early as possible, because Chrome reads the manifest during load and
 * may not re-read it before offering the install.
 */
export function scopeManifestToTrip(slug: string, title?: string): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) return;

  const origin = window.location.origin;
  const manifest = {
    name: title || 'bixl trips',
    short_name: (title || 'trip').slice(0, 12),
    description: 'Private family itinerary.',
    start_url: `${origin}/${slug}`,
    scope: `${origin}/`,
    display: 'standalone',
    background_color: '#f3ecdf',
    theme_color: '#c2532a',
    icons: [
      { src: `${origin}/icon-192.png`, sizes: '192x192', type: 'image/png', purpose: 'any' },
      { src: `${origin}/icon-512.png`, sizes: '512x512', type: 'image/png', purpose: 'any' },
      { src: `${origin}/icon-maskable-512.png`, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
    ],
  };

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  const next = URL.createObjectURL(blob);
  const previous = link.href.startsWith('blob:') ? link.href : null;
  link.href = next;
  if (previous) URL.revokeObjectURL(previous);
}

/**
 * The permanent "Find this trip again" section.
 *
 * Deliberately separate from the banner: the banner is a one-time nudge that
 * dismisses itself and stays gone for a month, which is exactly wrong for
 * someone who needs the instructions six weeks later and cannot remember how
 * they got here. This never goes away and has no dismiss control.
 */
export function setupHowToSection(): void {
  const body = document.getElementById('trip-howto-body');
  if (!body) return;

  body.innerHTML = '';

  const intro = document.createElement('p');
  intro.className = 'muted small';
  intro.textContent = isHandheld()
    ? 'Two ways to get straight back to this trip without hunting for the link.'
    : 'Bookmark this page, or open it on your phone to add it to your home screen.';
  body.appendChild(intro);

  if (isHandheld()) {
    body.appendChild(howToBlock('Put an icon on your home screen', isIos() ? IOS_STEPS : ANDROID_STEPS));
  }
  body.appendChild(howToBlock('Save it as a bookmark', isIos() ? IOS_BOOKMARK_STEPS : ANDROID_BOOKMARK_STEPS));

  // The one instruction that survives a lost icon, a lost bookmark and a lost
  // text message — and the only one that can be given over the phone.
  const fallback = document.createElement('p');
  fallback.className = 'trip-howto-fallback small';
  const home = document.createElement('strong');
  home.textContent = window.location.host;
  fallback.append('Forgotten everything? Go to ', home, ' and this trip will be listed there.');
  body.appendChild(fallback);
}

function howToBlock(heading: string, steps: string[]): HTMLElement {
  const wrap = document.createElement('section');
  wrap.className = 'trip-howto-block';
  const h = document.createElement('h3');
  h.className = 'trip-howto-heading';
  h.textContent = heading;
  const ol = document.createElement('ol');
  for (const text of steps) {
    const li = document.createElement('li');
    li.textContent = text;
    ol.appendChild(li);
  }
  wrap.appendChild(h);
  wrap.appendChild(ol);
  return wrap;
}

/**
 * Names the iOS home-screen icon after the trip.
 *
 * iOS does not read the manifest when adding to the home screen, so the
 * per-trip name set in `scopeManifestToTrip` never reaches it — the label comes
 * from this meta tag, and while that was hardcoded every trip's icon on every
 * iPhone read "bixl trips". Safari reads the tag from the live DOM at the
 * moment Add to Home Screen is tapped, so setting it now is in time.
 *
 * The home screen truncates hard, so this stays short.
 */
export function setHomeScreenName(title?: string): void {
  if (!title) return;
  const tag = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  if (tag) tag.content = title.slice(0, 15);
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

// Registered at module load: Chrome fires this early, well before the itinerary
// has been fetched, and the event is only useful if it was not allowed to pass.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e as BeforeInstallPromptEvent;
});

/**
 * Shows the banner once the itinerary is on screen — installing from behind the
 * passcode form would just bookmark the passcode form.
 */
export function setupInstallBanner(): void {
  const banner = document.getElementById('install-banner') as HTMLElement | null;
  const steps = document.getElementById('install-steps') as HTMLElement | null;
  const stepsList = document.getElementById('install-steps-list') as HTMLOListElement | null;
  const stepsDone = document.getElementById('install-steps-done') as HTMLButtonElement | null;
  const button = document.getElementById('install-btn') as HTMLButtonElement | null;
  const dismiss = document.getElementById('install-dismiss') as HTMLButtonElement | null;
  const sub = document.getElementById('install-sub') as HTMLElement | null;
  if (!banner || !steps || !stepsList || !stepsDone || !button || !dismiss) return;

  const reason = suppressionReason();
  const forced = reason !== null && isForced();
  if (reason && !forced) {
    console.info(`[install] banner hidden: ${reason}. Add ?install to the URL to force it.`);
    return;
  }

  const close = (remember: boolean): void => {
    banner.hidden = true;
    steps.hidden = true;
    if (remember) rememberDismissed();
  };

  /**
   * Opening the steps is deliberately idempotent — never a toggle, and never
   * rebinding this button to something destructive. The first version turned it
   * into "Got it" in place, so a second tap (which the copy all but asked for)
   * dismissed the banner before anyone had read a word of it.
   */
  const openSteps = (): void => {
    if (!steps.hidden) return;
    stepsList.innerHTML = '';
    for (const text of isIos() ? IOS_STEPS : ANDROID_STEPS) {
      const li = document.createElement('li');
      li.textContent = text;
      stepsList.appendChild(li);
    }
    steps.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    steps.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  if (isIos()) {
    // No install API on iOS, so the button must not claim to be one.
    button.textContent = 'Show me';
    if (sub) sub.textContent = 'From Safari’s Share menu — opens straight to this trip.';
  }

  // Printed on the page, not just the console. This is meant to be read on the
  // phone the banner is misbehaving on, and reaching a phone's console means
  // tethering it to a Mac and opening Web Inspector.
  if (forced && sub) {
    sub.textContent = `Shown by ?install — normally hidden: ${reason}`;
  }

  button.setAttribute('aria-expanded', 'false');
  button.onclick = async () => {
    if (!deferredPrompt) {
      openSteps();
      return;
    }
    const prompt = deferredPrompt;
    // A deferred prompt is single-use; dropping the reference keeps a second
    // tap from calling prompt() on a spent event.
    deferredPrompt = null;
    button.disabled = true;
    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      // Either way the question has been asked and answered — stop asking, but
      // only for a while.
      close(outcome !== 'accepted');
    } catch {
      openSteps();
    } finally {
      button.disabled = false;
    }
  };

  // Dismissal lives only on controls that say so: the × and the steps' Done.
  stepsDone.onclick = () => close(true);
  dismiss.onclick = () => close(true);

  window.addEventListener('appinstalled', () => close(false));

  banner.hidden = false;
}
